import { useEffect, useMemo, useRef, useState } from 'react'

import { Code } from '@connectrpc/connect'
import { useTranslation } from 'react-i18next'

import { Alert } from '@enechain/ecloud-designsystem'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { RegionLoading } from '~/components/common/RegionLoading'
import {
  SilentCancelSubmitError,
  useCrudModalForm,
} from '~/hooks/useCrudModalForm'
import { useListBgMasters } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { TradingPartnerFormFields } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/components/TradingPartnerFormFields'
import { useGetTradingPartner } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/hooks/useGetTradingPartner'
import { useListTradingPartners } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/hooks/useListTradingPartners'
import { useUpdateTradingPartner } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/hooks/useUpdateTradingPartner'
import { isDuplicateTradingPartnerName } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/models/duplicateName'
import {
  type TradingPartnerFormInput,
  type TradingPartnerFormValues,
  buildTradingPartnerFormSchema,
} from '~/pages/AdminOcctoMaster/tabs/TradingPartner/models/schema'
import { resolveTradingPartnerDetailedError } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/models/serverError'
import type { TradingPartnerRow } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/models/types'
import { mapMasterGetError } from '~/pages/AdminOcctoMaster/tabs/_common/getError'

type Props = {
  isOpen: boolean
  row: TradingPartnerRow
  onClose: () => void
}

const TRADING_PARTNER_EDIT_ERROR_KEYS: Partial<Record<Code, string>> = {
  [Code.AlreadyExists]:
    'page:adminOcctoMaster.tradingPartner.serverError.alreadyExists',
  [Code.Aborted]: 'page:adminOcctoMaster.tradingPartner.serverError.aborted',
  [Code.NotFound]: 'page:adminOcctoMaster.tradingPartner.serverError.notFound',
  [Code.InvalidArgument]:
    'page:adminOcctoMaster.tradingPartner.serverError.invalidArgument',
  // AC-06 (spec AC-T08): Update (PUT) で計画マスタから参照中のアカウント
  // コードを除外すると FailedPrecondition が返る。 未マッピングのままだと
  // 汎用の "サーバーエラーが発生しました" に落ちてしまうため、 Delete と同じ
  // 専用メッセージへ揃える。
  [Code.FailedPrecondition]:
    'page:adminOcctoMaster.tradingPartner.serverError.failedPrecondition',
}

export const EditTradingPartnerModal = ({
  isOpen,
  row,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useUpdateTradingPartner()
  const {
    data,
    isPending: isFetching,
    isError,
    error,
  } = useGetTradingPartner(row.id)
  const fetched = data?.tradingPartner
  // AC-06 (spec AC-OMT-020): 名称完全一致の重複警告 dialog 用。 自身の行は
  // 判定対象から除外する (= 自己一致で誤警告しない)。 list 取得が未完了 /
  // エラーの場合は fail-open。
  const { data: partnerListData, isError: partnerListIsError } =
    useListTradingPartners()
  // AC-MOI-013 extension: 編集中の取引先が既に保持しているコードは重複判定
  // から除外し、それ以外のコードについて BG マスタと cross-check する。
  const { data: bgListData } = useListBgMasters()
  const existingBgCodeSet = useMemo(() => {
    const currentCodes = new Set<string>()
    if (fetched !== undefined) {
      for (const a of fetched.accounts) {
        currentCodes.add(a.code)
      }
    }
    const codes = new Set<string>()
    if (bgListData !== undefined) {
      for (const bg of bgListData.items) {
        if (!currentCodes.has(bg.bgCode)) {
          codes.add(bg.bgCode)
        }
      }
    }
    return codes
  }, [bgListData, fetched])
  const retainedCodeSet = useMemo(
    () => new Set(fetched?.accounts.map((a) => a.code) ?? []),
    [fetched],
  )
  const schema = useMemo(
    () => buildTradingPartnerFormSchema(existingBgCodeSet, retainedCodeSet),
    [existingBgCodeSet, retainedCodeSet],
  )
  const [duplicateNameConfirmOpen, setDuplicateNameConfirmOpen] =
    useState(false)
  // AC-06 (spec AC-OMT-020): 重複警告 dialog のユーザー回答を onSubmit 内で
  // await するための resolver。 dialog の「登録」/「キャンセル」ボタンが
  // これを呼んで onSubmit の続きを再開させる。
  const duplicateConfirmResolverRef = useRef<
    ((proceed: boolean) => void) | null
  >(null)

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    reset,
    setValue,
    isDirty,
  } = useCrudModalForm<TradingPartnerFormInput, TradingPartnerFormValues>({
    schema,
    defaultValues: {
      name: '',
      shortName: '',
      accounts: [],
    },
    // AC-06 (spec AC-OMT-020): 名称完全一致の重複判定は Zod validation
    // 通過後の onSubmit (= handleSubmit の onValid) 内で行う (自身の行は
    // 除外)。 フォームに Zod error が残っている状態で保存を押しても onSubmit
    // へは到達しない。 fail-open (list 未取得 / エラー) の場合は判定せず
    // 直接保存する。
    onSubmit: async (values) => {
      if (fetched?.updatedAt === undefined) {
        throw new Error(
          t('page:adminOcctoMaster.tradingPartner.serverError.unknown'),
        )
      }
      if (partnerListData !== undefined && !partnerListIsError) {
        if (
          isDuplicateTradingPartnerName(
            partnerListData.items,
            values.name,
            row.id,
          )
        ) {
          const proceed = await new Promise<boolean>((resolve) => {
            duplicateConfirmResolverRef.current = resolve
            setDuplicateNameConfirmOpen(true)
          })
          if (!proceed) {
            throw new SilentCancelSubmitError()
          }
        }
      }
      await mutateAsync({
        id: row.id,
        name: values.name,
        shortName: values.shortName,
        accounts: values.accounts.map((a) => ({
          areaCode: a.areaCode,
          code: a.code,
          codeName: a.codeName,
          isRepresentative: a.isRepresentative,
          weekdayContacts: a.weekdayContacts.map((c) => ({
            department: c.department,
            name: c.name !== '' ? c.name : undefined,
            phone: c.phone,
            email: c.email !== '' ? c.email : undefined,
          })),
          holidayContacts: a.holidayContacts.map((c) => ({
            department: c.department,
            name: c.name !== '' ? c.name : undefined,
            phone: c.phone,
            email: c.email !== '' ? c.email : undefined,
          })),
        })),
        expectedUpdatedAt: fetched.updatedAt,
      })
    },
    onClose,
    customErrorKeys: TRADING_PARTNER_EDIT_ERROR_KEYS,
    unknownErrorKey: 'page:adminOcctoMaster.tradingPartner.serverError.unknown',
    resolveDetailedError: resolveTradingPartnerDetailedError,
  })

  useEffect(() => {
    if (fetched !== undefined) {
      reset({
        name: fetched.name,
        shortName: fetched.shortName,
        accounts: fetched.accounts.map((a) => ({
          areaCode: a.areaCode,
          code: a.code,
          codeName: a.codeName,
          isRepresentative: a.isRepresentative,
          weekdayContacts: a.weekdayContacts.map((c) => ({
            department: c.department,
            name: c.name ?? '',
            phone: c.phone,
            email: c.email ?? '',
          })),
          holidayContacts: a.holidayContacts.map((c) => ({
            department: c.department,
            name: c.name ?? '',
            phone: c.phone,
            email: c.email ?? '',
          })),
        })),
      })
    }
  }, [fetched, reset])

  const handleConfirmDuplicateName = (): void => {
    setDuplicateNameConfirmOpen(false)
    duplicateConfirmResolverRef.current?.(true)
  }

  const handleCancelDuplicateName = (): void => {
    setDuplicateNameConfirmOpen(false)
    duplicateConfirmResolverRef.current?.(false)
  }

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      isSaveDisabled={isFetching || isError}
      onClose={handleClose}
      serverError={serverError}
      // design/admin/occto.html #tp-edit-modal: width:min(960px,94vw) 相当の
      // 広幅モーダル。 chakra dialog recipe に 960px 丁度の named size は無い
      // (xs→384/sm→448/md→448/lg→672/xl→896、 実測値)。 "2xl" は ecloud/chakra の
      // dialog recipe に size variant が存在せず maxWidth:none (= 実測で 100vw に
      // 展開、 既存 CsvImportModal 等の "2xl" 指定も同様に無効化されている) になる
      // ため使わず、 実際に maxW を制約する named size のうち最も広い "xl" (896px,
      // 実測) を採用する。
      size="xl"
      title={t('page:adminOcctoMaster.tradingPartner.modal.editTitle')}
    >
      {isError && (
        <Alert
          status="error"
          title={mapMasterGetError(
            t,
            error,
            'page:adminOcctoMaster.tradingPartner.serverError',
          )}
        />
      )}
      <RegionLoading isLoading={isFetching}>
        {isFetching ? null : (
          <TradingPartnerFormFields
            control={control}
            errors={errors}
            setValue={setValue}
          />
        )}
      </RegionLoading>
      {/* AC-06 (spec AC-OMT-020): 名称完全一致の重複警告。 「登録」で続行、
          「キャンセル」で dialog を閉じてモーダルに戻る (保存はしない)。 */}
      <ConfirmDialog
        body={t(
          'page:adminOcctoMaster.tradingPartner.form.duplicateNameConfirmBody',
        )}
        confirmLabel={t(
          'page:adminOcctoMaster.tradingPartner.form.duplicateNameConfirmLabel',
        )}
        isOpen={duplicateNameConfirmOpen}
        onCancel={handleCancelDuplicateName}
        onConfirm={handleConfirmDuplicateName}
        title={t(
          'page:adminOcctoMaster.tradingPartner.form.duplicateNameConfirmTitle',
        )}
      />
    </CrudModalLayout>
  )
}
